import { Component } from 'opus-angular-seed-core';

/*
    Language selector component, which allows us to select the language and enable/disable translations.
    - decouples internal data implementation from parent
    - bindings:
     languages (optional): Array of languages with an enabled flag,
     selected (optional): language code for selected one,
     onChange (callback): to sync the data to parent component
    - internal store: this._state (private)
*/
@Component({
    bindings: {
        languages: '=?',
        selected: '=?',
        onChange: '&?'
    },
    template: `
        <ul class="tabs01">
            <li ng-repeat="lang in vm.languages"
                ng-class="{ active: vm.selected===lang.label }"
                ng-click="vm.select(lang)">
                <span ng-class="{ active: vm.selected===lang.label, enabled: lang.enabled }">
                    <a>{{ lang.label }}</a>
                </span>
            </li>
        </ul>
        <label class="switch01 visible-label mt2em w150" ng-if="vm.selected" style="width: fit-content !important;">
            <input type="checkbox"
                   ng-checked="vm.isSelectedLanguageEnabled()"
                   ng-click="vm.toggleEnabledCheckbox()"
                   ng-disabled="false" />
            <span>{{ 'skill.Language enabled' | translate }}</span>
        </label>
    `
})
export default class {
    constructor (TokenService, lodash) {
        this.lodash = lodash;
        this.TokenService = TokenService;
        this.defaultLanguage = (this.TokenService.getCompanyLanguage() || '').toUpperCase();
        this.companySupportedLanguages = (this.TokenService.getCompanySupportedLanguages() || [])
            .map(l => (l || '').toUpperCase()).filter(Boolean);
    }

    normalizeLanguages (input) {
        const supported = this.companySupportedLanguages;
        const def = this.defaultLanguage;

        // If input is explicitly an array (even empty), use it; otherwise create default list
        let list = Array.isArray(input)
            ? input
            : supported.map(l => ({ label: l, enabled: l === def }));

        // force uppercase + shape, filter to supported, dedupe
        list = list.map(x => ({ label: (x && (x.label || x.code) || '').toUpperCase(), enabled: !!(x && x.enabled) }))
            .filter(x => supported.indexOf(x.label) !== -1);

        const seen = {};
        list = list.filter(x => (seen[x.label] ? false : (seen[x.label] = true)));

        // default first, then supported order
        const order = (a, b) => {
            if (a === def) return -1;
            if (b === def) return 1;
            return supported.indexOf(a) - supported.indexOf(b);
        };
        return list.sort((a, b) => order(a.label, b.label));
    }

    sortLanguages (input) {
        if (!Array.isArray(input)) return input;
        const def = this.defaultLanguage;
        const match = input.find(l => l.label === def);

        // If default not found or already first, return as is
        if (!match || (input.length > 0 && input[0].label === def)) {
            return input;
        }

        // Move default to front, preserve order of others
        return [match, ...input.filter(l => l !== match)];
    }

    syncSelectedState () {
        if (!this.selected) return;
        const current = (this.languages || []).find(l => l.label === this.selected);
        const on = current ? !!current.enabled : false;
        if (!this._state) this._state = {};
        if (!this._state[this.selected]) this._state[this.selected] = { enabled: on };
        else this._state[this.selected].enabled = on;
    }

    $onInit () {
        if (!this._state) this._state = {};

        // If parent provided languages, use them as-is (don't normalize/replace)
        // Parent is responsible for providing correct language structure
        if (!Array.isArray(this.languages) || this.languages.length === 0) {
            this.languages = this.normalizeLanguages(this.languages);
        }
        else {
            this.languages = this.sortLanguages(this.languages);
        }

        // Initialize _state for all languages
        this.languages.forEach(lang => {
            const k = lang.label;
            if (!this._state[k]) this._state[k] = { enabled: !!lang.enabled };
        });

        // Set selected if not provided or invalid
        if (!this.selected || !this.languages.find(l => l.label === this.selected)) {
            this.selected = this.defaultLanguage || (this.languages[0] && this.languages[0].label);
        }

        // Sync the checkbox state with the selected tab
        this.syncSelectedState();

        // Emit initial state
        if (this.onChange && this.selected && this._state[this.selected]) {
            this.onChange({ lang: this.selected, data: this._state[this.selected] });
        }

        // Store previous selected for comparison in $doCheck
        this._prevSelected = this.selected;
        this._prevLanguages = this.languages;
    }

    $doCheck () {
        // Check if languages array reference has changed
        if (this.languages !== this._prevLanguages) {
            this._prevLanguages = this.languages;

            // DO NOT normalize/replace the languages array - parent controls it
            // Just update _state based on current languages
            if (!this._state) this._state = {};
            this.languages.forEach(lang => {
                const k = lang.label;
                if (!this._state[k]) {
                    this._state[k] = { enabled: !!lang.enabled };
                }
                else {
                    // Update enabled state from languages array
                    this._state[k].enabled = !!lang.enabled;
                }
            });
        }

        // Check if selected has changed
        if (this.selected !== this._prevSelected) {
            this._prevSelected = this.selected;
            // Ensure _state exists for the newly selected language
            if (this.selected && !this._state[this.selected]) {
                const langEntry = this.languages.find(l => l.label === this.selected);
                if (langEntry) {
                    this._state[this.selected] = { enabled: !!langEntry.enabled };
                }
            }
            this.syncSelectedState();
        }
    }

    $onChanges (changes) {
        let shouldEmit = false;

        // Process languages first to ensure _state is populated
        if (changes.languages) {
            // DO NOT normalize/replace the languages array - parent controls it
            // Only normalize if parent provided empty or invalid array
            if (!Array.isArray(this.languages) || this.languages.length === 0) {
                this.languages = this.normalizeLanguages(this.languages);
            }
            else {
                this.languages = this.sortLanguages(this.languages);
            }

            if (!this._state) this._state = {};
            const allowed = {};
            this.languages.forEach(lang => {
                const k = lang.label;
                allowed[k] = true;
                if (!this._state[k]) this._state[k] = { enabled: !!lang.enabled };
                // Do NOT overwrite existing enabled state - preserve what parent set
            });
            Object.keys(this._state).forEach(k => {
                if (!allowed[k]) {
                    delete this._state[k];
                }
            });

            if (!this.selected || !allowed[this.selected]) {
                this.selected = (this.languages[0] && this.languages[0].label) || this.defaultLanguage;
            }

            shouldEmit = true;
        }

        // Process selected change (languages might not have changed this time)
        if (changes.selected && changes.selected.currentValue) {
            if (!this._state) this._state = {};

            // Ensure languages is initialized if not already
            if (!this.languages || this.languages.length === 0) {
                this.languages = this.normalizeLanguages(this.languages);
            }

            // Initialize all languages in _state if needed
            this.languages.forEach(lang => {
                const k = lang.label;
                if (!this._state[k]) this._state[k] = { enabled: !!lang.enabled };
            });

            // Ensure _state entry exists for the newly selected language
            if (this.selected && !this._state[this.selected]) {
                const langEntry = this.languages.find(l => l.label === this.selected);
                if (langEntry) {
                    this._state[this.selected] = { enabled: !!langEntry.enabled };
                }
            }

            shouldEmit = true;
        }

        // Always sync after any change
        if (changes.languages || changes.selected) {
            this.syncSelectedState();
        }

        // Emit change after initialization or updates
        if (shouldEmit && this.onChange && this.selected && this._state[this.selected]) {
            this.onChange({ lang: this.selected, data: this._state[this.selected] });
        }
    }

    getSortedLanguages () {
        const arr = [];
        if (this.defaultLanguage) arr.push({ label: this.defaultLanguage, enabled: true });
        this.lodash.forEach(this.companySupportedLanguages, lang => {
            if (lang !== this.defaultLanguage) arr.push({ label: lang, enabled: false });
        });
        return arr;
    }

    select (lang) {
        if (!lang) return;
        this.selected = lang.label;
        // align state to the tab being clicked (so checkbox reflects the dot)
        this.syncSelectedState();
        this.emitChange();
    }

    emitChange () {
        const k = this.selected;
        const data = this._state[k];
        if (this.onChange) this.onChange({ lang: k, data });
    }

    isSelectedLanguageEnabled () {
        if (!this.selected) {
            return false;
        }
        // Ensure _state exists and has entry for selected language
        if (!this._state) this._state = {};
        if (!this._state[this.selected]) {
            const langEntry = this.languages.find(x => x.label === this.selected);
            this._state[this.selected] = { enabled: langEntry ? !!langEntry.enabled : false };
        }
        const langEntry = this.languages.find(x => x.label === this.selected);
        return langEntry ? !!langEntry.enabled : false;
    }

    toggleEnabledCheckbox () {
        if (!this.selected) {
            return;
        }
        // Ensure _state exists and has entry for selected language
        if (!this._state) this._state = {};
        if (!this._state[this.selected]) {
            const langEntry = this.languages.find(x => x.label === this.selected);
            this._state[this.selected] = { enabled: langEntry ? !!langEntry.enabled : false };
        }
        // Toggle the enabled state
        const langEntry = this.languages.find(x => x.label === this.selected);
        if (langEntry) {
            langEntry.enabled = !langEntry.enabled;
            this._state[this.selected].enabled = langEntry.enabled;
            this.emitChange();
        }
    }

    toggleEnabled (label) {
        const k = (label || '').toUpperCase();
        const enabled = !!(this._state && this._state[k] && this._state[k].enabled);
        const langEntry = this.languages.find(x => x.label === k);
        if (langEntry) langEntry.enabled = enabled;
        this.emitChange();
    }
}



// WEBPACK FOOTER //
// ./src/app/components/language/selector/index.js